<template>
  <div class="contract-history-section">
    <div v-if="venueGroups.length > 0">
      <!-- Venue Dropdown -->
      <div class="venue-selector mb-4">
        <el-select
          v-model="selectedVenueId"
          placeholder="Select a venue"
          style="width: 100%"
        >
          <el-option
            v-for="group in venueGroups"
            :key="group.venueId"
            :label="group.venueName + (group.activeContract ? ' ✓' : '')"
            :value="group.venueId"
          />
        </el-select>
      </div>

      <div v-if="selectedGroup" class="history-list">
        <div class="venue-history-group">
          <!-- Venue Header -->
          <div class="venue-header">
            <h4 class="venue-name">{{ selectedGroup.venueName }}</h4>
            <div class="venue-header-actions">
              <button
                v-if="selectedGroup.activeContract"
                class="btn btn-outline-primary btn-xs edit-contract-btn mr-1"
                @click="$emit('edit-contract', selectedGroup.venueId, selectedGroup.activeContract)"
                title="Edit Contract"
              >
                <i class="fa fa-edit"></i> Edit
              </button>
              <button
                v-if="selectedGroup.activeContract && selectedGroup.activeContract.attachments && selectedGroup.activeContract.attachments.length"
                class="btn btn-outline-secondary btn-xs mr-1"
                :disabled="downloadingId === selectedGroup.venueId"
                @click="downloadContract(selectedGroup)"
                title="Download"
              >
                <i class="fa fa-download"></i>
              </button>
              <button
                class="btn btn-outline-success btn-xs"
                @click="openUploadModal(selectedGroup.venueId)"
                title="Upload Contract"
              >
                <i class="fa fa-upload"></i> Upload Contract
              </button>
            </div>
          </div>

          <!-- History Items -->
          <div class="history-items" v-if="selectedGroup.history.length > 0">
            <div
              v-for="(item, idx) in selectedGroup.history"
              :key="idx"
              class="history-item"
            >
              <div class="history-icon">
                <i :class="historyIcon(item.type)"></i>
              </div>
              <div class="history-event">
                {{ item.event }}
                <span
                  v-if="item.version > 1"
                  class="badge badge-secondary ml-1"
                  style="font-size:10px"
                >v{{ item.version }}</span>
                <el-tooltip
                  v-if="item.type === 'change_requested' && item.change_details"
                  :content="item.change_details"
                  placement="top"
                  effect="dark"
                  popper-class="change-details-tooltip"
                >
                  <i class="fa fa-info-circle text-info change-info-btn"></i>
                </el-tooltip>
              </div>
              <div class="history-timestamp">{{ formatTimestamp(item.timestamp) }}</div>
            </div>
          </div>

          <!-- No history yet for this venue -->
          <div v-else class="history-items">
            <div class="history-item">
              <div class="history-icon">
                <i class="fa fa-circle text-muted"></i>
              </div>
              <div class="history-event text-muted">No contract yet</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-history">
      <p class="text-muted">No contract history available.</p>
    </div>

    <!-- Upload Contract Modal -->
    <upload-contract-modal
      :visible="showUploadModal"
      @file-selected="onFileSelected"
      @close="showUploadModal = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { Message, MessageBox, Select, Option } from 'element-ui';
import UploadContractModal from '@/components/Contracts/UploadContractModal.vue';

export default {
  name: 'ContractHistoryDisplay',
  components: { UploadContractModal, [Select.name]: Select, [Option.name]: Option },
  props: {
    venues: {
      type: Array,
      default: () => [],
    },
    leadEventId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      contractsData: {},
      downloadingId: null,
      showUploadModal: false,
      pendingUploadVenueId: null,
      selectedVenueId: null,
    };
  },
  computed: {
    venueGroups() {
      const list = (this.contractsData.data || []);
      return this.venues.map(venue => {
        const data = list.find(v => String(v.venue_id) === String(venue.id)) || {};
        const activeContract = data.active_contract || null;
        const history = (data.history || []).slice().sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        return {
          venueId: venue.id,
          venueName: venue.name,
          activeContract,
          history,
        };
      });
    },
    selectedGroup() {
      if (!this.selectedVenueId) return null;
      return this.venueGroups.find(g => g.venueId === this.selectedVenueId) || null;
    },
  },
  watch: {
    venueGroups(groups) {
      this.setDefaultVenue(groups);
    },
  },
  async created() {
    await this.reload();
  },
  methods: {
    setDefaultVenue(groups) {
      if (!groups || !groups.length) return;
      // Prefer a venue with an active contract, else fall back to first
      const withContract = groups.find(g => g.activeContract);
      this.selectedVenueId = (withContract || groups[0]).venueId;
    },
    async reload() {
      try {
        const data = await this.$store.dispatch('contracts/fetchContracts', {
          leadEventId: this.leadEventId,
        });
        this.contractsData = data || {};
        this.setDefaultVenue(this.venueGroups);
      } catch {
        // non-blocking — show empty state
      }
    },

    async downloadContract(group) {
      const attachments = group.activeContract?.attachments || [];
      const attachment = attachments.find(a => a.type === 'client_signed_copy')
        || attachments.find(a => a.type === 'vc_signed_copy')
        || attachments.filter(a => a.type === 'initial_contract').pop()
        || attachments[0];
      if (!attachment) return;

      this.downloadingId = group.venueId;
      try {
        const res = await this.$store.dispatch('contracts/getDownloadUrl', {
          leadEventId: this.leadEventId,
          attachmentId: attachment.id,
        });
        const url = (res && (res.download_url || res.url)) || res;
        window.open(url, '_blank', 'noopener');
      } catch {
        Message.error('Could not generate download link. Please try again.');
      } finally {
        this.downloadingId = null;
      }
    },

    historyIcon(type) {
      const map = {
        contract_created:  'fa fa-plus text-primary',
        contract_sent:     'fa fa-paper-plane text-warning',
        contract_opened:   'fa fa-eye text-info',
        contract_signed:   'fa fa-check text-success',
        change_requested:  'fa fa-edit text-warning',
      };
      return map[type] || 'fa fa-circle text-muted';
    },

    openUploadModal(venueId) {
      this.pendingUploadVenueId = venueId;
      this.showUploadModal = true;
    },

    onFileSelected(file) {
      this.showUploadModal = false;
      this.$emit('upload-contract', this.pendingUploadVenueId, file);
      this.pendingUploadVenueId = null;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const m = moment.utc(timestamp).local();
      const diff = moment().diff(m, 'days');
      const time = m.format('h:mm A');
      if (diff === 0) return `${time} - Today`;
      if (diff === 1) return `${time} - Yesterday`;
      return `${time} - ${m.format('D MMM YYYY')}`;
    },
  },
};
</script>

<style scoped>
.venue-history-group {
  margin-bottom: 30px;
}

.venue-header {
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.venue-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.venue-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-contract-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
  font-size: 10px;
  padding: 4px 8px;
}

.edit-contract-btn:hover {
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-xs {
  font-size: 10px;
  padding: 4px 8px;
}

.history-items {
  padding-left: 0;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.history-item:last-child {
  border-bottom: none;
}

.history-icon {
  width: 25px;
  text-align: center;
  margin-right: 10px;
  flex-shrink: 0;
  font-size: 14px;
}

.history-event {
  flex: 1;
  font-weight: 400;
  color: #333;
}

.change-info-btn {
  margin-left: 6px;
  cursor: pointer;
  font-size: 13px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.change-info-btn:hover {
  opacity: 1;
}

.history-timestamp {
  font-size: 13px;
  color: #666;
  text-align: right;
  flex-shrink: 0;
}

.no-history p {
  text-align: center;
  padding: 16px 0;
}
</style>

<style>
.change-details-tooltip {
  max-width: 320px;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}
</style>
